import { formatDate as formatFoundationDate, toDate as toFoundationDate, type DateInput } from "@its-engineering-and-development/ui-foundation/utils";
import { DATE_EMPTY_VALUE, GLOBAL_DATE_FORMAT, TABLE_DATE_FORMAT_CONFIG, type DateFormatConfig } from "@/constants/date-formats";

export function isIsoDateKey(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function toDisplayDate(value: DateInput | null | undefined): Date | null {
    if (value == null) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    if (typeof value === "string" && isIsoDateKey(value)) {
        const localDate = new Date(`${value}T00:00:00`);
        return Number.isNaN(localDate.getTime()) ? null : localDate;
    }

    return toFoundationDate(value);
}

/**
 * Parses a date-like value to a millisecond timestamp.
 *
 * Returns `NaN` for missing or unparseable values so callers can decide how to
 * treat them (e.g. push them to the end of a sorted list).
 */
export function toDateTimestamp(value: DateInput | null | undefined): number {
    const date = toDisplayDate(value);
    return date ? date.getTime() : NaN;
}

/**
 * Formats a date-like value using a centralized token.
 * Returns `emptyValue` for missing or invalid values.
 */
export function formatDateWithToken(value: DateInput | null | undefined, token: string, emptyValue = ""): string {
    const date = toDisplayDate(value);
    if (!date) return emptyValue;
    return formatFoundationDate(date, token) || emptyValue;
}

/**
 * Formats table-visible dates as MM/DD/YYYY.
 * Returns an em dash for empty or invalid values.
 */
export function formatDate(value: DateInput | null | undefined): string {
    return formatDateWithToken(value, GLOBAL_DATE_FORMAT, DATE_EMPTY_VALUE);
}

/**
 * Comparator that orders date-like values from newest to oldest.
 *
 * Safe for ISO strings, timestamps and `Date` objects; invalid/missing dates are
 * sorted last. Use with `Array.prototype.sort`.
 */
export function compareDatesDesc(a: DateInput | null | undefined, b: DateInput | null | undefined): number {
    const timeA = toDateTimestamp(a);
    const timeB = toDateTimestamp(b);
    if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
    if (Number.isNaN(timeA)) return 1;
    if (Number.isNaN(timeB)) return -1;
    return timeB - timeA;
}

/**
 * Converts a display date string to YYYY-MM-DD (ISO), or null if invalid.
 * The format is driven by `config` (default: MM/DD/YYYY).
 * Validates month (1–12), day (1–31), year (1900–2100), and calendar reality
 * (e.g. rejects Feb 30).
 */
export function parseDisplayDateToIso(input: string, config: DateFormatConfig = TABLE_DATE_FORMAT_CONFIG): string | null {
    if (!input.trim()) return null;
    const [s0, s1, s2] = config.slotSizes;
    const sep = config.separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const slot = (n: number) => (n >= 3 ? `\\d{${n}}` : `\\d{1,${n}}`);
    const regex = new RegExp(`^(${slot(s0)})${sep}(${slot(s1)})${sep}(${slot(s2)})$`);
    const match = input.trim().match(regex);
    if (!match) return null;

    const parts: Record<string, number> = {};
    config.partOrder.forEach((field, i) => {
        parts[field] = parseInt(match[i + 1], 10);
    });
    const { month, day, year } = parts as { month: number; day: number; year: number };

    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (year < 1900 || year > 2100) return null;
    const testDate = new Date(year, month - 1, day);
    if (testDate.getFullYear() !== year || testDate.getMonth() !== month - 1 || testDate.getDate() !== day) return null;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Formats an ISO date string for display inside a text input using the given format config.
 * Returns "" (not the em dash) for null/invalid so the input stays empty.
 * Defaults to MM/DD/YYYY.
 */
export function formatIsoDateForInput(isoDate: string | null | undefined, config: DateFormatConfig = TABLE_DATE_FORMAT_CONFIG): string {
    if (!isoDate) return "";
    const date = toDisplayDate(isoDate);
    if (!date) return "";
    return formatFoundationDate(date, config.formatToken) || "";
}

/**
 * Auto-formats a raw digit stream into a display date by inserting separators at
 * the field boundaries defined by `config` (default: slashes at positions 2 and 4
 * for MM/DD/YYYY). Non-digit characters are stripped and the result is capped at
 * the total digit count (slotSizes[0]+[1]+[2]). Does not handle deletion — callers
 * should pass the raw value through unchanged when the input length decreases.
 */
export function autoFormatDateInput(raw: string, config: DateFormatConfig = TABLE_DATE_FORMAT_CONFIG): string {
    const [s0, s1, s2] = config.slotSizes;
    const { separator } = config;
    const digits = raw.replace(/\D/g, "").slice(0, s0 + s1 + s2);
    if (digits.length <= s0) return digits;
    if (digits.length <= s0 + s1) return `${digits.slice(0, s0)}${separator}${digits.slice(s0)}`;
    return `${digits.slice(0, s0)}${separator}${digits.slice(s0, s0 + s1)}${separator}${digits.slice(s0 + s1)}`;
}
